import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserDropdown from '../UserDropdown';
import ReportForm from '../Shared/ReportForm';
import MyReports from '../Shared/MyReports';

const RiderHelpSupport = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showMyReports, setShowMyReports] = useState(false);
  const [reportKey, setReportKey] = useState(0);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const faqSections = [
    {
      title: 'Getting Started',
      items: [
        {
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password" on the login page and enter your email address. You\'ll receive an email with a link to reset your password. The link expires after 4 hours for security purposes.'
        },
        {
          question: 'How do I update my profile information?',
          answer: 'Navigate to Settings from the menu (hamburger icon in the top right). From there, you can update your name, phone number, and other profile details.'
        }
      ]
    },
    {
      title: 'Booking Rides',
      items: [
        {
          question: 'How do I book a ride?',
          answer: 'Navigate to "Browse Drivers" from your dashboard. Select a driver you\'d like to ride with, then choose a pickup date and click "Load Schedule" to see their availability. Select an available time slot, enter your pickup and dropoff locations, click "Get Quote" to see the price, and complete your payment to confirm the booking.'
        },
        {
          question: 'What is a day booking?',
          answer: 'A day booking is a half-day (up to 6 hours) or full-day (up to 12 hours) reservation with a driver at a flat rate. This is ideal for events, airport trips with multiple stops, or when you need a driver for an extended period. Pricing is based on the total mileage across all your stops.'
        },
        {
          question: 'How do I cancel a ride?',
          answer: 'Go to your dashboard and find the ride in your "Upcoming Rides" section. Click on the ride to view details, then select "Cancel Ride". Please note that cancellation policies may vary, and you may be charged a cancellation fee depending on how close to the pickup time you cancel.'
        },
        {
          question: 'What happens if my driver cancels?',
          answer: 'If your driver cancels, you\'ll receive a notification immediately. Your payment method will not be charged. We recommend rebooking with another available driver as soon as possible.'
        }
      ]
    },
    {
      title: 'Managing Rides',
      items: [
        {
          question: 'How do I view my ride history?',
          answer: 'From your dashboard, click on "Ride History" (or "View Ride History" card). This shows all your past rides including completed rides, cancelled rides, and their details such as pickup/dropoff locations, dates, and amounts paid.'
        },
        {
          question: 'How do I contact my driver?',
          answer: 'Once a ride is confirmed, you can view your driver\'s contact information in the ride details. Click on any upcoming ride from your dashboard to see the driver\'s phone number and other details.'
        },
        {
          question: 'How do I add a driver to my favorites?',
          answer: 'When browsing drivers, click the heart icon on any driver\'s card to add them to your favorites. Favorite drivers appear in the "Book Now" dropdown on your dashboard for quick booking. You can have up to 5 favorite drivers.'
        }
      ]
    },
    {
      title: 'Payments & Pricing',
      items: [
        {
          question: 'How is ride pricing calculated?',
          answer: 'Ride pricing is based on the driver\'s rates, which include a base fare plus a per-mile charge. The exact price depends on the distance between your pickup and dropoff locations, the day of the week, and the time of day. You\'ll always see the total price before confirming your booking.'
        },
        {
          question: 'What payment methods are accepted?',
          answer: 'We accept all major credit and debit cards through our secure payment processor (Stripe). Your card information is never stored on our servers.'
        },
        {
          question: 'When will my payment method be charged?',
          answer: 'Your payment method is charged after the ride is completed. If you cancel early (24+ hours before pickup or within 5 minutes of booking), your payment method will not be charged. If your driver cancels, your payment method will not be charged. Late cancellations may result in a partial charge.'
        },
        {
          question: 'How do I add a tip for my driver?',
          answer: 'After your ride is completed, you can add a tip from your Ride History page. Find the completed ride and click the "Add Tip" button. Tips go directly to your driver and are greatly appreciated!'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/rider/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Help & Support</h1>
            </div>
            <UserDropdown userType="rider" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Support</h2>
          <p className="text-gray-600 mb-4">
            Need help? Our support team is here for you.
          </p>
          <a
            href="mailto:support@ridehowl.com"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            support@ridehowl.com
          </a>
        </div>

        {/* Submit a Report */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Submit a Report</h2>
            <button
              onClick={() => setShowReportForm(!showReportForm)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              {showReportForm ? 'Hide Form' : 'New Report'}
            </button>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Report bugs, safety concerns, inappropriate behavior, payment disputes, or other issues.
          </p>
          {showReportForm && (
            <ReportForm
              userType="rider"
              onSuccess={() => {
                setReportKey(prev => prev + 1);
                setShowMyReports(true);
              }}
              onCancel={() => setShowReportForm(false)}
            />
          )}
        </div>

        {/* My Reports */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <button
            onClick={() => setShowMyReports(!showMyReports)}
            className="w-full flex items-center justify-between"
          >
            <h2 className="text-lg font-semibold text-gray-900">My Reports</h2>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${showMyReports ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          {showMyReports && (
            <div className="mt-4">
              <MyReports key={reportKey} />
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h2>
          </div>

          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="border-b border-gray-200 last:border-b-0">
              <button
                onClick={() => toggleSection(sectionIndex)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
              >
                <span className="font-medium text-gray-900">{section.title}</span>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSection === sectionIndex ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>

              {expandedSection === sectionIndex && (
                <div className="px-6 pb-4">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="mb-4 last:mb-0">
                      <h4 className="font-medium text-gray-800 mb-2">{item.question}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default RiderHelpSupport;
